/**
 * 应用骨架：**弹性三段式** + 极简视图切换（大厅 ⇄ 牌桌）。
 *
 * 不引 react-router：M1 只有两个视图，路由表的体积和心智成本都不划算。
 * 视图状态在 uiStore.view；需要深链接（/local、/history 这些 PWA shortcut）时
 * 由 main.tsx 在启动时读一次 location.pathname 映射成初始 view。
 *
 * 大厅 / 牌桌的**内容**由各自的 agent 通过 props 插进来，App 不 import 它们的文件，
 * 这样四个模块可以并行开发、互不阻塞编译。
 *
 * ── 这一版改了两件结构性的事 ────────────────────────────────
 *
 * 1. **底部区不再是固定高度**（P1/P2）。骨架从 grid 换成 flex：顶栏固定、舞台 flex:1、
 *    底部随内容。底部四态由 `.dock[data-state]` 表达，没有操作时直接收到 0，
 *    那块屏幕交还给舞台 —— 大厅与等待室因此能用满整个视口，不再「中间挤、下面空」。
 *
 * 2. **删掉了横屏拒绝渲染**（P4）。旧版在 `height < 500` 时盖一层全屏的「转屏提示」，
 *    把折叠机展开态（691×634，DEVICES.md D1 实测）当成「拿反了」直接挡在门外，
 *    违反 R2「平板档是一等布局，不要对这种尺寸弹请旋转」。现在横屏/方屏一律按平板档
 *    正常渲染；真的小到放不下时由 `useViewportMode().tooSmall` 给出**具体原因**，
 *    交给上层做非阻塞提示，而不是在这里一刀切地拒绝渲染。
 */

import { useEffect, useLayoutEffect, useRef, useState } from 'react'
import type { ReactNode } from 'react'

import './theme/global.css'
import './theme/club.css'
import { useModalDialog } from './hooks/useModalDialog.js'
import { useSettingsStore, useTableStore, useUiStore } from './store/index.js'
import { applySettingsToDom } from './store/settingsStore.js'
import { TOPBAR_H, resolveStage } from './table/seatGeometry.js'

/**
 * 底部区四态。**下游组件请只用这四个值**，别自己写高度。
 *
 *   hidden   —— 没有任何操作：display:none，高度真的是 0（含安全区一起交还给舞台）
 *   bar      —— 只有一条细状态条（「等待其他玩家…」「本手已结束」）：高度由内容撑开
 *   actions  —— 对局中的行动区：预留统一的 --dock-h（64px，安全区另算）
 *   sheet    —— 行动区 + 浮在其上的底部抽屉（加注尺度面板）。
 *               ★ dock 的盒高与 actions **完全相同** —— 抽屉是 absolute 浮层，
 *                 展开时牌桌不会被顶上去，舞台高度在整手牌里恒定。
 */
export type DockState = 'hidden' | 'bar' | 'actions' | 'sheet'

export interface AppSlots {
  /** 顶栏内容；省略用默认（产品名 + 房间码） */
  readonly topBar?: ReactNode
  /** 大厅视图：渲染在 stage 区（弹性，吃满剩余高度） */
  readonly lobby?: ReactNode
  /** 大厅底部 CTA，渲染在 dock 区。为空时 dock 整个收起到 0。 */
  readonly lobbyDock?: ReactNode
  /**
   * 大厅 dock 的形态。省略时按内容自动判定：有内容 = `bar`（高度随内容），没内容 = `hidden`。
   * 大厅不该出现 `actions` —— 那是对局中为主按钮行预留的固定高度。
   */
  readonly lobbyDockState?: DockState
  /**
   * 牌桌视图。必须自己渲染两个区域：
   * `<div className="stage">…</div>` 与 `<div className="dock" data-state="…">…</div>`
   * （`.dock` 不写 data-state 时按 `actions` 处理，向后兼容）
   */
  readonly table?: ReactNode
  /** 遮牌页内容（本地传手机）。veiled 为真时才挂载。 */
  readonly veil?: ReactNode
  /** 额外覆盖层：Toast / Sheet / 断线条 */
  readonly overlays?: ReactNode
}

/** 读一次安全区实测值。custom property 里的 env() 不会被 getComputedStyle 解析，只能靠探针。 */
function measureSafeArea(): { top: number; bottom: number } {
  const doc = globalThis.document
  if (!doc?.body) return { top: 0, bottom: 0 }
  const probe = doc.createElement('div')
  probe.style.cssText =
    'position:fixed;left:0;top:0;width:0;visibility:hidden;pointer-events:none;' +
    'padding-top:env(safe-area-inset-top,0px);padding-bottom:env(safe-area-inset-bottom,0px)'
  doc.body.appendChild(probe)
  const cs = getComputedStyle(probe)
  const top = parseFloat(cs.paddingTop) || 0
  const bottom = parseFloat(cs.paddingBottom) || 0
  probe.remove()
  return { top, bottom }
}

/**
 * 把可视视口高度写进 `--app-h`（§11.2）。
 * **只在 resize 时写，不在 scroll 中写** —— iOS 上滚动期间 visualViewport 会连续变化，
 * 跟着写会让整个 shell 每帧重排。
 */
function useVisualViewportHeight(): void {
  useEffect(() => {
    const vv = globalThis.visualViewport
    const root = document.documentElement
    let frame = 0
    const write = (): void => {
      frame = 0
      const h = Math.round(vv?.height ?? globalThis.innerHeight)
      root.style.setProperty('--app-h', `${h}px`)
    }
    const schedule = (): void => {
      if (frame !== 0) return
      frame = requestAnimationFrame(write)
    }
    write()
    vv?.addEventListener('resize', schedule)
    globalThis.addEventListener('orientationchange', schedule)
    return () => {
      if (frame !== 0) cancelAnimationFrame(frame)
      vv?.removeEventListener('resize', schedule)
      globalThis.removeEventListener('orientationchange', schedule)
    }
  }, [])
}

/** 首帧密度估算。牌桌挂载后 useTableGeometry 会用实测值覆盖密度。 */
function useShellMetrics(layoutSeatCount: number): void {
  const setDensity = useUiStore((s) => s.setDensity)
  const safeRef = useRef<{ top: number; bottom: number } | null>(null)

  useLayoutEffect(() => {
    safeRef.current ??= measureSafeArea()
    const safe = safeRef.current
    let frame = 0
    const apply = (): void => {
      frame = 0
      // ★ 牌桌挂载后**绝不再写密度**：那时的唯一权威是 useTableGeometry 的实测舞台高度。
      //
      // resolveStage 只是个估算，它假定 dock 恰好 `--dock-h` 高；而对局中 dock 可能是
      // actions 也可能是 sheet（后者盒高不变，但估算模型并不知道这件事）。
      // 更糟的是这里**每次 resize 都写**：它会把牌桌刚写进去的实测值盖掉，而
      // TableScene 的 effect 只在 `layout.density` 发生**变化**时才写，
      // 值没变就不会来纠正 —— 错误的那一档会一直留在 .app-shell 上。
      if (useUiStore.getState().view === 'table') return
      const width = globalThis.innerWidth
      const height = globalThis.visualViewport?.height ?? globalThis.innerHeight
      setDensity(
        resolveStage({ width, height, safeTop: safe.top, safeBottom: safe.bottom }, layoutSeatCount)
          .density,
      )
    }
    const schedule = (): void => {
      if (frame !== 0) return
      frame = requestAnimationFrame(apply)
    }
    apply()
    globalThis.addEventListener('resize', schedule)
    globalThis.addEventListener('orientationchange', schedule)
    return () => {
      if (frame !== 0) cancelAnimationFrame(frame)
      globalThis.removeEventListener('resize', schedule)
      globalThis.removeEventListener('orientationchange', schedule)
    }
  }, [layoutSeatCount, setDensity])
}

function DefaultTopBar(): ReactNode {
  const roomCode = useTableStore((s) => s.roomCode)
  return (
    <>
      <strong style={{ fontSize: 'var(--fs-md)', fontWeight: 'var(--fw-bold)' }}>德州扑克</strong>
      {roomCode !== null && (
        <span className="code selectable" style={{ color: 'var(--c-gold)', fontSize: 'var(--fs-sm)' }}>
          {roomCode}
        </span>
      )}
    </>
  )
}

/**
 * slot 没传时的占位。**只写标题**：没有接线的空壳只在开发时出现，
 * 副标题写「TableScene 尚未接入」之类的内部话既帮不了用户，也没人会读。
 */
function Placeholder({ title }: { title: string }): ReactNode {
  return (
    <div
      style={{
        display: 'flex',
        flex: '1 1 auto',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 'var(--sp-2)',
        padding: 'var(--sp-6)',
        textAlign: 'center',
      }}
    >
      <div style={{ fontSize: 'var(--fs-lg)', fontWeight: 'var(--fw-bold)' }}>{title}</div>
    </div>
  )
}

export function App(slots: AppSlots = {}): ReactNode {
  const view = useUiStore((s) => s.view)
  const veiled = useUiStore((s) => s.veiled)
  const resync = useUiStore((s) => s.resync)
  const density = useUiStore((s) => s.density)
  const layoutSeats = useTableStore((s) => s.layoutSeats)
  const settings = useSettingsStore()
  const [leavingVeil, setLeavingVeil] = useState(false)
  const veilOpen = view === 'table' && (veiled || leavingVeil)
  const { dialogRef: veilRef, onDialogKeyDown: onVeilKeyDown } = useModalDialog({ open: veilOpen })

  useVisualViewportHeight()
  useShellMetrics(layoutSeats.length)

  // 设置 → data-* → tokens.css 变量。组件里因此没有任何 if(fourColorDeck) 分支。
  useLayoutEffect(() => {
    applySettingsToDom(settings)
  }, [settings])

  // resync 期间全局禁动效（§11.6），两帧后由 net 层复位；这里只负责挂 class
  useLayoutEffect(() => {
    document.documentElement.classList.toggle('resync', resync)
  }, [resync])

  // 遮罩退出要播 260ms 淡出；进入必须零动画（红线 #12）
  const prevVeiled = useRef(veiled)
  useEffect(() => {
    if (prevVeiled.current && !veiled) {
      setLeavingVeil(true)
      const t = setTimeout(() => setLeavingVeil(false), 260)
      prevVeiled.current = veiled
      return () => clearTimeout(t)
    }
    prevVeiled.current = veiled
    return undefined
  }, [veiled])

  const isTable = view === 'table'
  useEffect(() => {
    document.querySelector('meta[name="theme-color"]')?.setAttribute('content', isTable ? '#132820' : '#eee9df')
  }, [isTable])
  // 大厅底部：有内容就让内容决定高度，没内容就整个收起（P1）
  const lobbyDockState: DockState =
    slots.lobbyDockState ?? (slots.lobbyDock == null ? 'hidden' : 'bar')

  return (
    <div
      className="app-shell"
      data-density={density}
      data-view={isTable ? 'table' : 'flow'}
      /**
       * ★ 牌桌视图**不写** data-dock：形态只有 ActionDock 知道，由它命令式写上来
       * （见 action/ActionDock.tsx 的 useDockState）。
       * 以前这里恒写 'actions'，而等待室的 dock 其实是 bar 态（真实高度约 51px），
       * Toast 按 --dock-h 抬高后就悬在按钮行上 —— 那是一条线上实锤的遮挡 bug。
       */
      {...(isTable ? {} : { 'data-dock': lobbyDockState })}
    >
      <header className="topbar">{slots.topBar ?? <DefaultTopBar />}</header>

      {isTable ? (
        (slots.table ?? (
          <>
            <div className="stage">
              <Placeholder title="牌桌" />
            </div>
            <div className="dock" data-state="actions" />
          </>
        ))
      ) : (
        <>
          {/* stage--flow：文档流容器，吃满剩余高度。内容应按可用高度自适应，
              正常尺寸下不应出现内部滚动条（overflow 只是极端小屏的兜底）。 */}
          <main className="stage stage--flow">
            {slots.lobby ?? <Placeholder title="大厅" />}
          </main>
          {lobbyDockState !== 'hidden' && (
            <div className="dock" data-state={lobbyDockState}>
              {slots.lobbyDock}
            </div>
          )}
        </>
      )}

      {/* 遮牌页：只在牌桌视图挂载（大厅没有底牌要遮）。
          条件渲染而非 opacity —— 底牌组件在 veiled 期间必须整体不在 DOM 中（红线 #12 第 6 条）。
          初始值是 veiled=true，所以刷新后进牌桌一定先看到遮罩。 */}
      {veilOpen && (
        <div
          className={leavingVeil ? 'veil veil--leaving' : 'veil'}
          ref={veilRef}
          role="dialog"
          aria-modal="true"
          aria-label="传递设备"
          tabIndex={-1}
          onKeyDown={onVeilKeyDown}
        >
          {slots.veil ?? <Placeholder title="把手机交给下一位" />}
        </div>
      )}

      {slots.overlays}

      {/* 牌局播报：一个视觉隐藏的 live region（§11.10） */}
      <div className="sr-only" aria-live="polite" id="table-announcer" />
      <div className="sr-only" aria-live="assertive" id="turn-announcer" />
    </div>
  )
}

export default App
export { TOPBAR_H }
